import * as React from 'react';
import { filter, find, flow, get, last, map, startCase } from 'lodash';
import { connect } from 'react-redux';
import { IStore } from '../../../../../../redux/IStore';
import QuoteIcons from '../../../Components/Icon/QuoteIcons';
import { IPremium, ITraveller } from '../../../../../../data/api/models';
import { FormattedNumber } from 'react-intl';
import DefinitionPopup from '../../../../../../components/DefinitionPopup';
import { getTripDuration, replaceBenefitTextPlaceHolder } from '../../../../../../helpers/Quote';
import { actions as brandingActions } from '../../../../../../services/branding/reducer';
import { bindActionCreators } from 'redux';
import { IMtaState } from '../../../../../../services/mta/models';
import { IBrandingActions } from '../../../../../../services/branding/models';

interface IDisplayBenefits {
  text: string;
  shortName: string;
}

interface IPolicySummary {
  mta: IMtaState
  actions: {
    branding: IBrandingActions;
  }
}

const PolicySummary = ({ mta, actions }: IPolicySummary) => {

  const policy = mta.selectedPolicy;
  const product = actions.branding.getProduct()

  if (!policy || !product) {
    return;
  }

  const premium: IPremium = last(policy.premiums);
  const travellers: ITraveller[] = policy?.travellers || []
  const isAnnual = policy.metadata.scheme_type === 'annual';
  const region = policy.metadata.region;
  const destinationsValue = policy.metadata.destinations;
  const title = isAnnual ? 'Region' : 'Destinations'
  const coverLevel = premium?.scheme?.coverLevel
  const options = policy.metadata.options;
  const startDate = policy.commencesAt;
  const endDate = policy.expiresAt;


  const renderLevelOfCover = () => {
    const benefitsToDisplay: IDisplayBenefits[] = [
      { text: 'Medical Emergency Cover up to ', shortName: 'Medical Expenses' },
      { text: 'Cancellation or cutting short your trip up to ', shortName: 'Trip Cancellation' },
      { text: 'Personal Belongings Cover up to ', shortName: 'Personal Belongings' },
      { text: 'Loss of money and documents up to ', shortName: 'Money & Documents' }
    ]

    const travelDuration = isAnnual ? (
      <div className={'cover-level-list'}>
        <QuoteIcons name={'Tick'} color={'blue'}/>
        <div>Unlimited travel for up to 31 days per trip</div>
      </div>
    ) : '';

    let iconColor = 'darkBlue';

    switch (coverLevel) {
      case 'gold':
        iconColor = 'gold';
        break;
      case 'platinum':
        iconColor = 'platinum'
        break;
      default:
        break;
    }

    return (
      <div className={'policy-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{'Level of cover'}</div>
          <div className={'flex--stretch'}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'ShieldTicked'} color={iconColor}/></div>
              <div className={'traveller-info single'}>
                <div className={'title'}>{startCase(`${coverLevel} Cover`)}</div>
              </div>
            </div>
            {travelDuration}

            {map(benefitsToDisplay, (benefit: IDisplayBenefits, key) => {
              const selectedBenefit = find(get(premium, 'benefits'), ['shortName', benefit.shortName]);
              const displayValue = get(selectedBenefit, `coverLevelDisplay.${coverLevel}`);
              return (
                <div className={'cover-level-list'} key={key}>
                  <QuoteIcons name={'Tick'} color={'blue'}/>
                  <div className={'benefit-text'}>{benefit.text + displayValue}</div>
                </div>
              )
            })}

            <div className={'cover-level-list'}>
              <QuoteIcons name={'Tick'} color={'blue'}/>
              <div className={'benefit-text'}>{'Legal Liability'}</div>
            </div>

            <div className={'cover-level-list'}>
              <QuoteIcons name={'Tick'} color={'blue'}/>
              <div className={'benefit-text'}>{'Legal Protection'}</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderTravellers = () => {
    return (
      <div className={'policy-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>Travellers</div>
          <div className={'flex--stretch'}>
            {map(travellers, (traveller, index) => {
              return (
                <>
                  <div className={'traveller-info-container'} key={index}>
                    <div><QuoteIcons name={'Person'} color={'blue'}/></div>
                    <div className={'traveller-info'}>
                      <div
                        className={'title'}>{traveller.title + ' ' + traveller.firstName + ' ' + traveller.lastName}</div>
                      <div>{traveller.dob}</div>
                    </div>
                  </div>
                </>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  const renderDestination = () => {
    return (
      <div className={'policy-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{title}</div>
          <div className={'flex--stretch'}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'Globe'} color={'blue'}/></div>
              <div className={'traveller-info single'}>
                <div
                  className={'title'}>{isAnnual ? startCase(region) : (destinationsValue ? destinationsValue.toString() : '')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderOptionIcons = (optionName: string) => {
    return (<QuoteIcons name={optionName} color={'blue'}/>);
  }

  const renderExtraCover = () => {
    const addedOptionNumber = filter(options, (o) => {
      return o === true;
    }).length

    const additionalCover = {
      cruise_cover: get(product, 'metadata.options.cruise_cover'),
      winter_sports: get(product, 'metadata.options.winter_sports'),
      gadget_cover: get(product, 'metadata.options.gadget_cover'),
      excess_waiver: get(product, 'metadata.options.excess_waiver'),
      golf_cover: get(product, 'metadata.options.golf_cover'),
      enhanced_trip_disruption: get(product, 'metadata.options.enhanced_trip_disruption'),
    };

    return (
      <div className={'policy-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{'Extra cover'}</div>
          <div className={'flex--stretch'}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'ShieldPlus'} color={'blue'}/></div>
              <div className={'traveller-info'}>
                <div className={'title'}>{addedOptionNumber + ' upgrades added'}</div>
              </div>
            </div>
          </div>
        </div>

        {map(additionalCover, (option, optionKey) => {

          const isGolfCover = optionKey === 'golf_cover';
          const isGadgetCover = optionKey === 'gadget_cover';
          const isCruiseCover = optionKey === 'cruise_cover';
          const isWinterSportsCover = optionKey === 'winter_sports';
          const isEnhancedTripDisruption = optionKey === 'enhanced_trip_disruption';
          const text = get(product, `metadata.definitions.${optionKey}_alt_title_new`, get(product, `metadata.definitions.${optionKey}_alt_title`))
          let bodyText = ((isGolfCover || isCruiseCover || isWinterSportsCover) && get(product, 'metadata')) ? text : product.metadata.definitions[`${optionKey}_alt_${coverLevel}`];

          if (isEnhancedTripDisruption) {
            bodyText = get(product, 'metadata.definitions.enhanced_trip_disruption', '').split('Do you require coverage for these situations?')[0]
          }

          if (isWinterSportsCover) {
            const wsBenefit = find(get(premium, 'benefits', []), ['name', 'Winter sports'])
            const skiEquipment = get(wsBenefit, 'subBenefits', []).find((sb) => sb.name.includes('Ski equipment'))
            if (get(skiEquipment, 'text', null)) {
              bodyText = bodyText.replace('{alt_value}', get(skiEquipment, 'text'))
            }
          }

          if (isGadgetCover) {
            const extraGadgetText = get(product, `metadata.definitions.${optionKey}_alt_dynamic_new`)
            const dynamicText = replaceBenefitTextPlaceHolder(extraGadgetText, get(premium, 'benefits', []), 'gadget_cover', false, coverLevel)
            bodyText = bodyText + '\n\n' + dynamicText;
          }

          if (get(options, optionKey)) {
            const totalPrice = get(premium, `information.boltOns.${optionKey}.total`)
            const currency = get(premium, `currency`)

            const formatted = (
              <FormattedNumber
                value={totalPrice}
                style="currency"
                currency={currency}
              />
            )

            return (
              <div className={'flex-container'} key={optionKey}>
                <div className={'flex--20'}/>
                <div className={'flex--stretch'}>
                  <div className={'traveller-info-container'}>
                    <div>{renderOptionIcons(optionKey)}</div>
                    <div className={'traveller-info single'}>
                      <div>{option.name}</div>
                      <div className={'sub-title'}>Added for {formatted} total</div>
                    </div>
                  </div>
                </div>
                <div className={'info-popup'}>INFO <DefinitionPopup
                  name="benefit-info"
                  definition={bodyText}
                  iconType="info"
                /></div>
              </div>
            )
          }
          return null
        })}

      </div>
    )
  }

  const renderDates = () => {
    const tripDuration = isAnnual ? 365 : getTripDuration(startDate, endDate, 1);

    return (
      <div className={'policy-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{'Cover start'}</div>
          <div className={'flex--stretch'}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'Calendar'} color={'blue'}/></div>
              <div className={'traveller-info'}>
                <div className={'title'}>{`${tripDuration} days cover`}</div>
                <div>{`Starting ${startDate}`}</div>
                {!isAnnual && (
                  <div>{`Ending ${endDate}`}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {renderTravellers()}
      {renderDestination()}
      {renderLevelOfCover()}
      {renderExtraCover()}
      {renderDates()}
    </>
  )
}

export default flow([
  connect(
    (state: IStore) => ({
      mta: state.mta,
    }),
    (dispatch) => ({
      actions: {
        branding: bindActionCreators({ ...brandingActions }, dispatch),
      },
    }),
  ),
])(PolicySummary);
